/**
 * Encatch Web SDK
 * 
 * A lightweight, type-safe SDK for integrating Encatch forms and surveys.
 * This module creates the _encatch global object that queues commands
 * until the remote implementation script loads.
 */

import type {
  EncatchSDK,
  EncatchConfig,
  UserTraits,
  IdentifyOptions,
  EventCallback,
  Command,
} from './types';
import { loadRemoteScript } from './loader';
import { createQueuedMethod, QUEUED_METHODS } from './queue';

// Re-export types for consumers
export type {
  EncatchSDK,
  EncatchConfig,
  UserTraits,
  IdentifyOptions,
  StartSessionOptions,
  EventType,
  EventCallback,
  EventPayload,
  Theme,
  ResetMode,
  ShowFormOptions,
} from './types';

/**
 * Creates the Encatch SDK stub instance
 * All method calls are queued until setProject() is called and the
 * remote implementation script loads.
 */
function createEncatchStub(): EncatchSDK {
  // Command queue for calls made before remote script loads
  const queue: Command[] = [];
  
  // Event callbacks - all callbacks receive all form events
  const eventCallbacks: EventCallback[] = [];

  // Create the SDK object
  const sdk: EncatchSDK = {
    // Internal state
    _q: queue,
    _eventCallbacks: eventCallbacks,
    _initialized: false,
    _apiKey: null,
    _config: {},

    // init is special - it triggers script loading
    init(apiKey: string, config?: EncatchConfig): void {
      if (sdk._initialized) {
        console.warn('[Encatch] SDK already initialized. Ignoring init call.');
        return;
      }

      sdk._apiKey = apiKey;
      sdk._config = config || {};
      sdk._initialized = true;

      // Queue this call for the remote implementation
      queue.push(['init', apiKey, config]);

      // Load the remote script
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        loadRemoteScript(sdk._config).catch((error) => {
          console.error('[Encatch] Failed to initialize SDK:', error);
        });
      }
    },

    // Event subscription - receives all form events
    on(callback: EventCallback): () => void {
      eventCallbacks.push(callback);

      // Return unsubscribe function
      return () => {
        const index = eventCallbacks.indexOf(callback);
        if (index > -1) {
          eventCallbacks.splice(index, 1);
        }
      };
    },

    // Stub implementations - will be replaced by remote script
    identifyUser: () => {},
    setLocale: () => {},
    setCountry: () => {},
    setTheme: () => {},
    trackEvent: () => {},
    trackScreen: () => {},
    startSession: () => {},
    resetUser: () => {},
    showForm: () => {},
    dismissForm: () => {},
    addToResponse: () => {},
  };

  // Replace stub methods with queue-based versions
  // Skip init (handled specially above) and on (handled locally)
  const methodsToQueue = QUEUED_METHODS.filter(m => m !== 'init');
  
  for (const method of methodsToQueue) {
    // Use type assertion to assign queued methods
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (sdk as any)[method] = createQueuedMethod(queue, method);
  }

  return sdk;
}

/**
 * Initialize the global _encatch object
 * This runs immediately when the script loads
 */
function initGlobal(): EncatchSDK {
  // Check for existing instance (may have been created by snippet)
  if (typeof window !== 'undefined') {
    // If _encatch already exists with a queue, preserve it
    const existing = window._encatch;
    if (existing && Array.isArray(existing._q)) {
      // Merge existing queue into new instance
      const sdk = createEncatchStub();
      sdk._q.push(...existing._q);
      
      // Copy over any existing callbacks
      if (Array.isArray(existing._eventCallbacks)) {
        sdk._eventCallbacks.push(...existing._eventCallbacks);
      }
      
      // Copy initialization state
      if (existing._initialized) {
        sdk._initialized = existing._initialized;
        sdk._apiKey = existing._apiKey;
        sdk._config = existing._config || {};
      }
      
      window._encatch = sdk;
      return sdk;
    }

    // Create fresh instance
    const sdk = createEncatchStub();
    window._encatch = sdk;
    return sdk;
  }

  // Non-browser environment
  return createEncatchStub();
}

// Initialize and export
export const _encatch = initGlobal();

// Default export for convenience
export default _encatch;
